/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import type { InfoDialogLinksProps } from '../store/types';

export interface DatasetTimeseriesRequest {
  title: string;
  collectionLinks: InfoDialogLinksProps[];
  targetMapId?: string;
}

export type AddDatasetTimeseries = (
  request: DatasetTimeseriesRequest,
) => Promise<void>;

interface DatasetActions {
  addTimeseries?: AddDatasetTimeseries;
  isAddingTimeseries: boolean;
}

const DatasetActionsContext = React.createContext<DatasetActions>({
  isAddingTimeseries: false,
});

export const DatasetActionsProvider: React.FC<{
  onAddTimeseries?: AddDatasetTimeseries;
  children: React.ReactNode;
}> = ({ onAddTimeseries, children }) => {
  const [isAddingTimeseries, setIsAddingTimeseries] = React.useState(false);
  const isAdding = React.useRef(false);
  const addTimeseries = React.useCallback<AddDatasetTimeseries>(
    async (request) => {
      if (isAdding.current || !onAddTimeseries) {
        return;
      }
      isAdding.current = true;
      setIsAddingTimeseries(true);
      try {
        await onAddTimeseries(request);
      } finally {
        isAdding.current = false;
        setIsAddingTimeseries(false);
      }
    },
    [onAddTimeseries],
  );
  const value = React.useMemo(
    () => ({
      addTimeseries: onAddTimeseries ? addTimeseries : undefined,
      isAddingTimeseries,
    }),
    [onAddTimeseries, addTimeseries, isAddingTimeseries],
  );
  return (
    <DatasetActionsContext.Provider value={value}>
      {children}
    </DatasetActionsContext.Provider>
  );
};

export const useDatasetActions = (): DatasetActions =>
  React.useContext(DatasetActionsContext);
